import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCalendarAlt,
  FaChartLine,
  FaCreditCard,
  FaTimesCircle,
} from 'react-icons/fa';
import { influencerSubscriptionService } from '../services/influencerSubscriptionService';
import { useInfluencerCheckout } from '../context/InfluencerCheckoutContext';
import { INSTA_TOOL_PLAN } from '../../insta-tools/data/instaToolPricing.config';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const formatPlanName = (plan) => {
  if (!plan) return '—';
  const map = { free: 'Free', premium: 'Premium', basic: 'Basic' };
  return map[plan] || plan.charAt(0).toUpperCase() + plan.slice(1);
};

const StatCard = ({ icon, title, value }) => (
  <div className="bg-orange-50 rounded-xl p-2.5 flex items-center gap-2">
    <div className="p-1.5 bg-orange-100 rounded-lg text-orange-500 text-base">{icon}</div>
    <div>
      <p className="text-xs text-gray-500">{title}</p>
      <p className=" text-sm font-semibold text-gray-800">{value}</p>
    </div>
  </div>
);

/**
 * Insta Tools subscription billing block — plan details, cancel-at-period-end, or upgrade.
 */
const InfluencerSubscriptionBilling = ({ className = '' }) => {
  const { openCheckout } = useInfluencerCheckout();
  const [subscription, setSubscription] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const loadSubscription = useCallback(async (cacheBust = false) => {
    setBillingLoading(true);
    setBillingError(null);
    try {
      const res = await influencerSubscriptionService.getSubscription(
        cacheBust ? { cacheBust: true } : {}
      );
      if (res?.success && res?.data?.subscription) {
        setSubscription(res.data.subscription);
      } else {
        setSubscription(null);
      }
    } catch (err) {
      setBillingError(err.message || 'Failed to load subscription');
      setSubscription(null);
    } finally {
      setBillingLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  const handleCancelSubscription = async () => {
    if (cancelLoading || subscription?.cancelAtPeriodEnd) return;
    setShowCancelDialog(false);
    setCancelLoading(true);
    setBillingError(null);
    try {
      const res = await influencerSubscriptionService.cancelSubscription();
      if (res?.data?.subscription) {
        setSubscription(res.data.subscription);
      } else {
        await loadSubscription(true);
      }
    } catch (err) {
      setBillingError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelLoading(false);
    }
  };

  const isPaidActive =
    subscription?.planType === 'premium' && subscription?.isActive;
  const isFreePlan =
    !subscription || subscription.planType === 'free' || !subscription.isActive;

  return (
    <div className={className}>
      {billingLoading ? (
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 py-6 text-gray-500">
          <div className="h-5 w-5 animate-spin rounded-full border-t-2 border-b-2 border-orange-500" />
          <span>Loading subscription…</span>
        </div>
      ) : billingError ? (
        <div className="bg-red-50 rounded-xl p-4 text-red-700 text-sm">{billingError}</div>
      ) : subscription ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatCard
              icon={<FaCreditCard />}
              title="Active plan"
              value={formatPlanName(subscription.planType)}
            />
            <StatCard
              icon={<FaCalendarAlt />}
              title="Billing cycle"
              value={
                subscription.billingCycle
                  ? subscription.billingCycle.charAt(0).toUpperCase() +
                    subscription.billingCycle.slice(1)
                  : '—'
              }
            />
            <StatCard
              icon={<FaChartLine />}
              title="Status"
              value={
                subscription.cancelAtPeriodEnd
                  ? 'Cancelling at period end'
                  : subscription.isActive
                  ? 'Active'
                  : subscription.status || '—'
              }
            />
            <StatCard
              icon={<FaCalendarAlt />}
              title="Access until"
              value={
                subscription.isActive && subscription.expiryDate
                  ? formatDate(subscription.expiryDate)
                  : '—'
              }
            />
          </div>

          {subscription.cancelAtPeriodEnd && (
            <p className="text-amber-700 bg-amber-50 rounded-lg px-4 py-2 inline-flex items-center gap-2 text-sm">
              <FaTimesCircle />
              Your subscription will end at the close of the current billing period. Premium
              access continues until {formatDate(subscription.expiryDate)}.
            </p>
          )}

          {isPaidActive && !subscription.cancelAtPeriodEnd && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-xl bg-orange-50">
                <div>
                  <h3 className="text-sm md:text-base font-semibold text-black">
                    Cancel subscription
                  </h3>
                  <p className="text-[10px] tracking-tight md:text-sm text-gray-700 mt-0.5">
                    Keep premium access until your current billing period ends.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={cancelLoading}
                  className="shrink-0 px-5 py-2.5 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {cancelLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Cancelling…
                    </span>
                  ) : (
                    'Cancel'
                  )}
                </button>
              </div>
            </div>
          )}

          {isFreePlan && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-xl bg-gray-50">
                <div>
                  <h3 className="text-sm md:text-base font-semibold text-gray-900">
                    Upgrade to Premium
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 mt-0.5">
                    Unlock Insta Tools automation, analytics, and more.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => openCheckout(INSTA_TOOL_PLAN)}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Upgrade
                  </button>
                  <Link
                    to="/insta-tools/pricing"
                    className="px-5 py-2.5 text-sm font-medium text-center text-gray-800 border border-gray-300 rounded-lg hover:bg-white transition-colors"
                  >
                    View plans
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="text-gray-500 text-sm">No subscription data available.</p>
      )}

      {showCancelDialog && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => !cancelLoading && setShowCancelDialog(false)}
          role="presentation"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="cancel-subscription-title"
          >
            <h3
              id="cancel-subscription-title"
              className="text-lg font-semibold text-black mb-2"
            >
              Cancel subscription
            </h3>
            <p className="text-gray-700 text-sm leading-relaxed mb-6">
              Cancel at the end of the current billing period? You will keep premium access
              until {formatDate(subscription?.expiryDate)}.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelDialog(false)}
                disabled={cancelLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-60"
              >
                Go back
              </button>
              <button
                type="button"
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {cancelLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Cancelling…
                  </span>
                ) : (
                  'Yes, cancel'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfluencerSubscriptionBilling;
